import React, { useEffect, useState } from "react";
import StoreAPI from "../../../api/StoreApi";
import { showSuccess, showError } from "../../../utils/toastUtils";
import { useLoader } from "../../../utils/LoaderUtils";
import useUsernameInput from "../../../hooks/useUsernameInput";
import PaymentGatewayCard from "./cards/PaymentGatewayCard";
import GATEWAY_CONFIG from "../../../constants/paymentGatewayConstants";

function PaymentGatewaySettings() {
  useUsernameInput();

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [gateways, setGateways] = useState({
    cod: {
      enabled: false,
      priority: "3",
      additionalSettings: {
        maxOrderValue: "",
      },
    },
    partial: {
      enabled: false,
      priority: "4",
      additionalSettings: {
        advanceGateway: "razorpay",
        advanceType: "flat",
        advanceValue: "100",
        minimumAdvanceAmount: "1",
        minimumOrderAmount: "0",
        customerMessage: "Pay a small advance now to confirm your order. Pay the rest on delivery.",
      },
    },
    razorpay: {
      enabled: true,
      priority: "1",
      additionalSettings: {
        apiKey: "",
        apiSecret: "",
        mode: "live",
        webhookSecret: "",
      },
    },
    payu: {
      enabled: false,
      priority: "2",
      additionalSettings: {
        apiKey: "",
        saltKey: "",
        mode: "",
        webhookSecret: "",
      },
    },
  });

  const handleFieldChange = (gateway, field, value) => {
    setGateways((prev) => ({
      ...prev,
      [gateway]: {
        ...prev[gateway],
        additionalSettings: {
          ...(prev[gateway]?.additionalSettings || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleToggle = (gatewayName) => {
    setGateways((prev) => ({
      ...prev,
      [gatewayName]: {
        ...prev[gatewayName],
        enabled: !prev[gatewayName].enabled,
      },
    }));
  };

  useEffect(() => {
    fetchGatewayDetails();
  }, []);

  const fetchGatewayDetails = async () => {
    showLoader();

    try {
      const res = await StoreAPI.paymentGatewayDetails();

      if (res.code === 200) {
        const formatted = formatFromBackend(res.data);

        setGateways((prev) => ({
          ...prev,
          ...formatted,
        }));
      }
    } catch (err) {
      showError("Failed to fetch settings");
    } finally {
      hideLoader();
    }
  };

  const formatFromBackend = (data) => {
    const formatted = {};

    data.forEach((g) => {
      const config = GATEWAY_CONFIG[g.gatewayName];
      if (!config) return;

      const additionalSettings = {};

      if (config.fields) {
        config.fields.forEach((field) => {
          if (
            field.name === "apiKey" ||
            field.name === "apiSecret" ||
            field.name === "saltKey"
          ) {
            additionalSettings[field.name] =
              g.isApiKeyExists === true ? "********" : "";
          } else if (field.name === "webhookSecret") {
            additionalSettings[field.name] =
              g.isWebhookSecretExists === true ? "********" : "";
          } else if (field.name === "mode") {
            additionalSettings[field.name] = g.mode || "test";
          } else if (
            g.gatewayName === "cod" &&
            (field.name === "maxOrderValue" ||
              field.name === "availablePinCodes")
          ) {
            if (field.name === "availablePinCodes") {
              additionalSettings[field.name] =
                g.codSettings?.availablePinCodes || "";
            } else {
              additionalSettings[field.name] =
                g.codSettings?.maxOrderValue || "";
            }
          } else if (g.gatewayName === "partial") {
            additionalSettings[field.name] =
              g.partialSettings?.[field.name] ?? "";
          } else {
            additionalSettings[field.name] = g[field.name] || "";
          }
        });
      }

      formatted[g.gatewayName] = {
        enabled: g.enabled,
        priority: String(g.priority || "1"),
        additionalSettings,
      };
    });

    return formatted;
  };

  /**********VALDIATION FOR SUBMIT *************/
  const validate = () => {
    const newErrors = {};
    const enabled = Object.entries(gateways).filter(([_, g]) => g.enabled);

    if (!enabled.length) {
      newErrors.general = "At least one payment gateway must be enabled.";
    }

    enabled.forEach(([key, g]) => {
      const s = g.additionalSettings;

      if (key === "razorpay") {
        if (!s.apiKey) newErrors.razorpayKey = "Razorpay Key is required.";
        if (!s.apiSecret)
          newErrors.razorpaySecret = "Razorpay Secret is required.";
      }

      if (key === "payu") {
        if (!s.apiKey) newErrors.payuKey = "PayU Key is required.";
        if (!s.saltKey) newErrors.payuSalt = "PayU Salt is required.";
      }

      if (key === "partial") {
        if (!s.advanceGateway)
          newErrors.partialGateway = "Advance Gateway selection is required.";
        if (s.advanceValue === "" || Number(s.advanceValue) <= 0) {
          newErrors.partialValue = "Valid advance value is required.";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /********* BUILD PAYLAOD FOR SAVE DETAILS ************/
  const buildPayload = () => {
    const payload = [];
    const deleteGateways = [];

    Object.entries(gateways).forEach(([name, g]) => {
      const config = GATEWAY_CONFIG[name];
      if (!config) return;

      if (g.enabled) {
        const obj = {
          gatewayName: name,
          enabled: true,
          mode: g.additionalSettings.mode || "test",
          priority: String(g.priority || "1"),
          supportedMethods: [],
        };

        if (name === "cod") {
          obj.codSettings = {
            maxOrderValue: String(g.additionalSettings.maxOrderValue) || "",
            availablePinCodes: g.additionalSettings.availablePinCodes || "",
          };
        } else if (name === "partial") {
          obj.partialSettings = {
            advanceGateway: g.additionalSettings.advanceGateway || null,
            advanceType: g.additionalSettings.advanceType || "flat",
            advanceValue: Number(g.additionalSettings.advanceValue || 0),
            minimumAdvanceAmount: Number(
              g.additionalSettings.minimumAdvanceAmount || 0,
            ),
            minimumOrderAmount: Number(
              g.additionalSettings.minimumOrderAmount || 0,
            ),
            customerMessage: g.additionalSettings.customerMessage || "",
          };
        } else {
          config.fields.forEach((field) => {
            const value = g.additionalSettings[field.name];

            if (field.name === "saltKey") {
              obj.apiSecret = value;
            } else {
              obj[field.name] = value;
            }
          });
        }

        payload.push(obj);
      } else {
        deleteGateways.push(name);
      }
    });

    return { payload, deleteGateways };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!validate()) return;

    setIsLoading(true);
    showLoader();

    try {
      const { payload, deleteGateways } = buildPayload();

      const res = await StoreAPI.updatepaymentGatewayDetails({
        gateways: payload,
        deleteGateways,
      });

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>Payment Gateway Settings</h4>
      </div>
      <div className="card-body">
        <form method="post" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="col-12 mb-3">
              <div className="alert alert-danger py-2">{errors.general}</div>
            </div>
          )}

          {Object.entries(GATEWAY_CONFIG).map(([name, config]) => (
            <PaymentGatewayCard
              key={name}
              name={name}
              config={config}
              data={
                gateways[name] || { enabled: false, additionalSettings: {} }
              }
              errors={errors}
              onToggle={handleToggle}
              onChange={handleFieldChange}
            />
          ))}

          <div className="row">
            <div className="col-12 d-flex justify-content-end">
              {!isLoading && (
                <button type="submit" className="btn btn-primary shadow-none">
                  Submit
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default PaymentGatewaySettings;
